import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import { dt } from 'yoda-core-components/lib/helpers/Utils/GetTailwindToken';
import { bool } from 'prop-types';
import Icon from 'yoda-core-components/lib/components/Icon/next';
import Button from 'yoda-core-components/lib/components/Button';
import IconTriangle from '../../assets/svg/Triangle.svg';
import CMImageRenderer from '../CMImageRenderer/CMImageRenderer';
import { getDeviceType as getDeviceTypeSelector } from '../../selectors/CMPageSelector';
import { commonAutomationIds, ctaAutomationIds } from '../CoreMediaPage/CMPage.config';

import { metaDataElement, metaDataProperty } from '../../utils/cmPreviewMetaData';

const StoryProduct = (props) => {
    const data = get(props, 'data', props);
    const { automationId } = data;
    const bgColor = get(data, 'styles.backgroundFillColor', '');
    const isBgColorAvailable = bgColor !== 'ffffff';
    const containerColor = `#${bgColor}`;
    const imageGraphicAvailable = get(data, 'imageGraphic', {});
    const messageGraphicAvailable = get(data, 'messageGraphic', {});
    const styles = get(data, 'styles', {});
    const teaserTitle = get(data, 'teaserTitle', '');
    const teaserSubtitle = get(data, 'teaserSubtitle', '');
    const legalTextData = get(data, 'legalText', {});
    const ctaData = get(data, 'ctaLinks', []);
    const pictures = [...data?.pictures];
    const componentType = get(data, 'componentType', '');
    const { isCMPreview } = props;
    const [ctaDisplay, setCtaDisplay] = useState(false);
    const deviceType = useSelector((state) => getDeviceTypeSelector(state));

    const componetStylingResp = {
        padding: {
            S: `${dt(['m-6'])}`,
            M: `${dt(['m-12'])}`,
            L: `${dt(['m-20'])}`,
            None: `${dt(['m-0'])}`,
        },
        fontSize: {
            'heading-1': `${dt(['heading-1'])}`,
            'heading-2': `${dt(['heading-2'])}`,
            'heading-3': `${dt(['heading-3', 'lg:leading-[60px]'])}`,
            'heading-4': `${dt(['heading-4'])}`,
            'heading-5': `${dt(['heading-5'])}`,
            'heading-6': `${dt(['heading-6'])}`,
        },
        subTitleFontSize: {
            'body-1': `${dt(['text-normal'])}`,
            'body-2': `${dt(['text-xlarge'])}`,
        },
        headlineAlignment: {
            Left: `${dt(['text-left'])}`,
            Center: `${dt(['text-center'])}`,
            Right: `${dt(['text-right'])}`,
        },
        subheadlineAlignment: {
            Left: `${dt(['text-left'])}`,
            Center: `${dt(['text-center'])}`,
            Right: `${dt(['text-right'])}`,
        },
        ImageGraphicAlignment: {
            'top-right': `${dt(['top-6', 'right-6'])}`,
            'top-left': `${dt(['top-6', 'left-6'])}`,
            'bottom-right': `${dt(['bottom-6', 'right-6'])}`,
            'bottom-left': `${dt(['bottom-6', 'left-6'])}`,
        },
        MessageLockUpAlignment: {
            Left: `${dt(['items-start'])}`,
            Center: `${dt(['items-center', 'text-center'])}`,
            Right: `${dt(['justify-end', 'items-end', 'text-right'])}`,
        },
        PicturePlacement: {
            left: `${dt(['order-none'])}`,
            right: `${dt(['order-1'])}`,
        },
        CAtaSection: {
            Left: 'justify-start',
            Center: 'justify-center',
            Right: 'justify-end',
        },
        pictureStyles: {
            sm: {
                '1x1': `${dt(['sm:max-h-[719px]', 'sm:max-w-[719px]'])}`,
                '5x7': `${dt(['sm:max-h-[1006px]', 'sm:max-w-[719px]'])}`,
                '7x5': `${dt(['sm:max-h-[513px]', 'sm:max-w-[719px]'])}`,
            },
            md: {
                '1x1': `${dt(['md:max-h-[608px]', 'md:max-w-[608px]'])}`,
                '5x7': `${dt(['md:max-h-[783px]', 'md:max-w-[561px]'])}`,
                '7x5': `${dt(['md:max-h-[528px]', 'md:max-w-[738px]'])}`,
            },
            lg: {
                '1x1': `${dt(['lg:max-h-[646px]', 'lg:max-w-[646px]'])}`,
                '5x7': `${dt(['lg:max-h-[773px]', 'lg:max-w-[552px]'])}`,
                '7x5': `${dt(['lg:max-h-[595px]', 'lg:max-w-[832px]'])}`,
            },
        },
    };

    const ctaSection = componetStylingResp?.CAtaSection[styles?.messageLockUpAlignment];
    const HeadlineAlignment =
        componetStylingResp?.headlineAlignment[styles?.messageLockUpAlignment];
    const SubHeadlineAlignment =
        componetStylingResp?.subheadlineAlignment[styles?.messageLockUpAlignment];
    const ImageGraphicAlignment =
        componetStylingResp?.ImageGraphicAlignment[styles?.imageGraphicAlignment];

    const MessageLockUpAlignment =
        componetStylingResp?.MessageLockUpAlignment[styles?.messageLockUpAlignment];
    const PicturePlacement = componetStylingResp?.PicturePlacement[styles?.picturePlacement];

    const componentCTALinkStyle = dt(['flex', 'w-auto', 'items-center', 'justify-center']);
    const componentCTABtnStyle = dt([
        'flex',
        'lg:w-[240px]',
        'md:w-[244px]',
        'sm:w-full',
        'mt-6',
        'items-center',
        'justify-center',
        'gap-1',
    ]);

    const HeadlineFontSize = componetStylingResp?.fontSize[styles?.headlineFontSize];
    const SubHeadlineFontSize = componetStylingResp?.subTitleFontSize[styles?.subHeadlineFontSize];
    const lgPicture = componetStylingResp.pictureStyles.lg[styles?.pictureAspectRatio.slice(-3)];
    const mdPicture = componetStylingResp.pictureStyles.md[styles?.pictureAspectRatio.slice(-3)];
    const smPicture = componetStylingResp.pictureStyles.sm[styles?.pictureAspectRatio.slice(-3)];

    const MediaObject = () => {
        return (
            <>
                <CMImageRenderer
                    imageURL={
                        pictures[0]?.useOriginalImage === true
                            ? pictures[0]?.fullyQualifiedUrl
                            : pictures[0]?.uriTemplate
                    }
                    altText={pictures[0]?.alt}
                    componentType={componentType}
                    imageAspectRatio={styles?.pictureAspectRatio}
                    imageStyles={dt(['w-full', 'h-full'])}
                    automationId={commonAutomationIds?.IMAGE}
                />
            </>
        );
    };

    const ImageGraphic = () => {
        const { data, description } = imageGraphicAvailable;
        const imageGraphicSrc = `${data?.uri}`;

        return (
            <div
                className={dt([
                    'absolute',
                    ImageGraphicAlignment,
                    'xl:max-w-[320px]',
                    'xl:h-[80px]',
                    'lg:max-w-[254px]',
                    'lg:h-[63.5px]',
                    'smMd:max-w-[160px]',
                    'smMd:h-[40px]',
                ])}
                data-cm-metadata={isCMPreview && metaDataElement(props?.data, 'pictures')}
                data-automation-id={commonAutomationIds?.IMAGEGRAPHIC}
            >
                <img
                    src={imageGraphicSrc}
                    alt={description}
                    title={description}
                    style={{ height: '100%' }}
                    data-cm-metadata={isCMPreview && metaDataProperty('data')}
                />
            </div>
        );
    };

    const MessageGraphic = () => {
        const { data, description } = messageGraphicAvailable;
        const messageGraphicSrc = `${data?.uri}`;

        return (
            <div
                className={dt([
                    'mb-2',
                    'flex',
                    'xl:max-w-[320px]',
                    'xl:max-h-[80px]',
                    'lg:max-w-[254px]',
                    'lg:max-h-[63.5px]',
                    'smMd:max-w-[160px]',
                    'smMd:max-h-[40px]',
                ])}
                data-cm-metadata={isCMPreview && metaDataElement(props?.data, 'pictures')}
                data-automation-id={commonAutomationIds?.MESSAGEGRAPHIC}
            >
                <img
                    src={messageGraphicSrc}
                    alt={description}
                    title={description}
                    data-cm-metadata={isCMPreview && metaDataProperty('data')}
                />
            </div>
        );
    };

    const TeaserTitle = () => {
        return (
            <div
                className={`${HeadlineFontSize} ${HeadlineAlignment} ${dt([
                    'font-open-sans-bold',
                    'text-black',
                    'break-words',
                    '-tracking-[2px]',
                ])}`}
                style={{ color: `#${styles?.headLineColor}`, inlineSize: '100%' }}
                data-cm-metadata={isCMPreview && metaDataProperty('teaserTitle')}
                data-automation-id={commonAutomationIds?.MAINTITLE}
            >
                {teaserTitle}
            </div>
        );
    };

    const TeaserSubtitle = () => {
        return (
            <div
                className={`${SubHeadlineFontSize} ${SubHeadlineAlignment} ${dt([
                    'mt-2',
                    'text-black',
                ])}`}
                style={{ color: `#${styles?.subHeadlineColor}` }}
                data-cm-metadata={isCMPreview && metaDataProperty('teaserSubtitle')}
                data-automation-id={commonAutomationIds?.MAINSUBTITLE}
            >
                {teaserSubtitle}
            </div>
        );
    };

    const LegalText = () => {
        const { value, openInNewTab } = legalTextData?.urlHolder;
        const linkTragetValue = openInNewTab ? '_blank' : '';
        return (
            <div
                className={dt(['text-xsmall', 'mt-2'])}
                data-cm-metadata={isCMPreview && metaDataProperty('legalText')}
                data-automation-id={commonAutomationIds?.LEGALTEXT}
            >
                <span
                    style={{ color: `#${styles?.legalTextColor}` }}
                    id="legalText"
                    data-cm-metadata={isCMPreview && metaDataProperty('legalText.legalText')}
                >
                    {legalTextData?.legalText}
                    &nbsp;{' '}
                    {!isEmpty(legalTextData?.legalLinkText) && (
                        <a
                            className={dt(['underline', 'text-gray-70'])}
                            aria-label={legalTextData?.legalLinkText}
                            target={linkTragetValue}
                            href={value}
                            alt={legalTextData?.legalAltText}
                            title={legalTextData?.legalAltText}
                            data-automation-id={commonAutomationIds?.LEGALLINK}
                            rel="noreferrer noopener"
                            style={{ color: `#${styles?.legalLinkColor}` }}
                            data-cm-metadata={
                                isCMPreview && metaDataProperty('legalText.legalLinkText')
                            }
                        >
                            {legalTextData?.legalLinkText}
                        </a>
                    )}
                </span>
            </div>
        );
    };

    const CtaComponent = () => {
        const isLink = styles?.ctaStyle === 'link';
        const isDropDown = styles?.ctaStyle === 'button' && ctaData?.length > 2;
        const isButton = styles?.ctaStyle !== 'link' && ctaData?.length <= 2;
        const dropbtnStyle = dt([
            'flex',
            'justify-center',
            'items-center',
            'bg-white',
            'text-black',
            'text-xLg',
            'border',
            'border-solid',
            'border-gray-40',
            'cursor-pointer',
            'w-60',
            'smMd:text-base',
            'md:max-w-[248px]',
            'smOnly:max-w-[240px]',
            'mt-6',
            'py-2.5',
            'px-4',
            'rounded',
            'whitespace-nowrap',
        ]);
        const dropdownContentStyle = dt([
            !ctaDisplay && 'hidden',
            'absolute',
            'bg-white',
            'w-60',
            'md:max-w-[248px]',
            'smOnly:max-w-[240px]',
            'shadow-elevation1',
            'z-[3]',
            ctaDisplay && 'block',
        ]);
        const dropdownContentStylehover = dt([
            'hidden',
            'absolute',
            'bg-white',
            'w-60',
            'md:max-w-[248px]',
            'smOnly:max-w-[240px]',
            'shadow-elevation1',
            'z-[3]',
            'group-hover:block',
        ]);
        const toggleButton = () => {
            setCtaDisplay(!ctaDisplay);
        };
        return (
            <div
                className={dt([
                    'flex',
                    'flex-wrap',
                    `${ctaSection}`,
                    isButton && 'gap-4',
                    isLink && 'mt-6',
                    isLink && 'lg:mt-4',
                    isButton && 'sm:w-full',
                    isLink && 'gap-y-4',
                    isLink && 'gap-x-4',
                ])}
            >
                {isLink &&
                    ctaData?.map((currentCta) => (
                        <>
                            <Button
                                className={componentCTALinkStyle}
                                id="componentCTA"
                                aria-label={currentCta?.ctaText}
                                target={currentCta?.urlHolder?.openInNewTab ? '_blank' : ''}
                                linkUrl={currentCta?.urlHolder?.value}
                                alt={currentCta?.ctaAltText}
                                buttonType="Text"
                                size="Lg"
                                type="button"
                                automationId={ctaAutomationIds?.LINK}
                                style={{ color: `#${styles?.ctaColor}` }}
                                data-cm-metadata={
                                    isCMPreview && metaDataProperty('ctaLinks.ctaText')
                                }
                            >
                                {currentCta?.ctaText}
                            </Button>
                        </>
                    ))}
                {isDropDown && (
                    <>
                        <div
                            className={dt(['relative', 'inline-block', 'group'])}
                            data-automation-id={ctaAutomationIds?.DROPDOWN}
                        >
                            <button
                                className={dropbtnStyle}
                                id="componentCTA_dropdown"
                                onClick={deviceType.isDesktop !== true && toggleButton}
                                data-automation-id={ctaAutomationIds?.DROPDOWNBTN}
                                type="button"
                            >
                                <span className={dt(['font-open-sans-semibold'])}>Shop All</span>
                                <Icon
                                    iconType="svg"
                                    className={dt(['ml-2'])}
                                    width="16px"
                                    height="16px"
                                    viewBox="0 0 16 16"
                                >
                                    <IconTriangle />
                                </Icon>
                            </button>
                            <div
                                data-automation-id={ctaAutomationIds?.DROPDOWNITEMS}
                                className={
                                    deviceType.isDesktop !== true
                                        ? `${dropdownContentStyle}`
                                        : `${dropdownContentStylehover}`
                                }
                            >
                                {ctaData?.map((currentCta) => (
                                    <a
                                        aria-label={currentCta?.ctaText}
                                        target={currentCta?.urlHolder?.openInNewTab ? '_blank' : ''}
                                        href={currentCta?.urlHolder?.value}
                                        alt={currentCta?.ctaAltText}
                                        title={currentCta?.ctaAltText}
                                        data-automation-id={ctaAutomationIds?.DROPDOWNLINK}
                                        rel="noreferrer noopener"
                                        style={{ color: `#${styles?.componentCtaColor}` }}
                                        className={dt([
                                            'text-black',
                                            'text-center',
                                            'py-2.5',
                                            'px-6',
                                            'no-underline',
                                            'block',
                                            'hover:bg-light-white',
                                            'whitespace-normal',
                                        ])}
                                        data-cm-metadata={
                                            isCMPreview && metaDataProperty('ctaLinks.ctaText')
                                        }
                                    >
                                        {currentCta?.ctaText}
                                    </a>
                                ))}
                            </div>
                        </div>
                    </>
                )}
                {isButton &&
                    ctaData?.map((currentCta, index) => (
                        <>
                            <Button
                                className={`${componentCTABtnStyle} ${dt(['smMd:mt-0'])} ${
                                    index === 0 && dt(['smMd:mt-6'])
                                }`}
                                aria-label={currentCta?.ctaText}
                                target={currentCta?.urlHolder?.openInNewTab ? '_blank' : ''}
                                linkUrl={currentCta?.urlHolder?.value}
                                alt={currentCta?.ctaAltText}
                                title={currentCta?.ctaAltText}
                                id="componentCTA_button"
                                automationId={ctaAutomationIds?.BUTTON}
                                buttonType="Secondary"
                                size="Lg"
                                type="button"
                                data-cm-metadata={
                                    isCMPreview && metaDataProperty('ctaLinks.ctaText')
                                }
                            >
                                {currentCta?.ctaText}
                            </Button>
                        </>
                    ))}
            </div>
        );
    };
    return (
        <div
            className={dt([
                'lg:flex',
                'md:flex',
                'w-full',
                'min-h-[200px]',
                'items-start',
                'lg:gap-[40px]',
                'md:gap-[24px]',
                'sm:gap-[16px]',
                isBgColorAvailable && 'lg:p-6',
                isBgColorAvailable && 'sm:p-6',
                isBgColorAvailable && 'smOnly:!w-[calc(100%+48px)]',
                isBgColorAvailable && 'smOnly:-mx-6',
            ])}
            style={{ backgroundColor: containerColor }}
            data-automation-id={automationId}
            data-cm-metadata={isCMPreview && metaDataElement(data)}
        >
            <div
                className={`${PicturePlacement} ${lgPicture} ${mdPicture} ${smPicture}  ${dt([
                    'lg:w-1/2',
                    'md:w-1/2',
                    'sm:w-full',
                    'relative',
                    !isBgColorAvailable && 'smOnly:!w-[calc(100%+48px)]',
                    !isBgColorAvailable && 'smOnly:-mx-6',
                ])}`}
            >
                <MediaObject />
                {!isEmpty(imageGraphicAvailable) && <ImageGraphic />}
            </div>
            <div
                className={dt([
                    'lg:w-1/2',
                    'md:w-1/2',
                    'sm:w-full',
                    'flex',
                    'flex-col',
                    'sm:mt-4',
                    'justify-center',
                    'lg:pr-4',
                    'self-stretch',
                    MessageLockUpAlignment,
                ])}
                data-automation-id={commonAutomationIds?.MESSAGECONTAINER}
                data-cm-metadata={isCMPreview && metaDataElement(data)}
            >
                {!isEmpty(messageGraphicAvailable) && <MessageGraphic />}
                {!!teaserTitle && <TeaserTitle />}
                {!!teaserSubtitle && <TeaserSubtitle />}
                {(!isEmpty(legalTextData?.legalLinkText) || !isEmpty(legalTextData?.legalText)) && (
                    <LegalText />
                )}
                {!isEmpty(ctaData) && <CtaComponent />}
            </div>
        </div>
    );
};

StoryProduct.propTypes = {
    isCMPreview: bool,
};
StoryProduct.defaultProps = {
    isCMPreview: false,
};

export default StoryProduct;
